import { TrashIcon, WarningIcon } from '@phosphor-icons/react';
import { CircleCheckIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Autocomplete, ItemStatusEnum } from '@/modules/common';
import { MultiSelect } from '@/modules/common/components/multi-select';
import { useFetchModelsData } from '@/modules/events/api';
import {
  EventPrinterStatus,
  positionOptions,
} from '@/modules/eventsV2/constants';
import { getModelGcrFileDetails } from '@/modules/events/helpers';
import { EventDetailsTab } from '@/modules/eventsV2/constants';
import { Routes } from '@/router/routes';

import { defaultFormValues } from '..';

export const useModels = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string; tab: EventDetailsTab }>();

  const { control, watch } = useFormContext();
  const [createGcrDetails, setCreateGcrDetails] = useState(null);
  const [activeFocusFieldId, setActiveFocusFieldId] = useState<string>('');

  const { optionsData, models, printers, gcrs, isFetching, refetchGcrs } =
    useFetchModelsData();

  const selectedPrinters = watch('printers', defaultFormValues.printers);
  const filteredPrinters = selectedPrinters.filter(
    (printer) =>
      printer.printerId && printer.status === EventPrinterStatus.Enabled
  );

  const fields = watch('models', defaultFormValues.models);

  const { remove, update, append } = useFieldArray({
    control,
    name: 'models',
  });

  const filteredData = useMemo(
    () => models.filter((item) => item.status === ItemStatusEnum.Ready),
    [models]
  );

  const getModelSelectedValue = useCallback(
    (modelId: string) => models.find((item) => item.id === modelId)?.name ?? '',
    [models]
  );

  const modelOptions = useMemo(
    () =>
      filteredData?.map((style) => ({
        value: style.name,
        id: style.id,
      })),
    [filteredData]
  );

  const gcrModelTypeMapping = Object.fromEntries(
    fields
      .filter((field) => field.modelId && field.sizeId)
      .map((field) => {
        const { typeId, emptyGcrFilePrinter } = getModelGcrFileDetails({
          models,
          printers,
          sizeId: field.sizeId,
          modelId: field.modelId,
          gcrs: gcrs.items,
          eventPrinters: filteredPrinters,
        });

        return [
          `${field.modelId}_${field.sizeId}`,
          { typeId, emptyGcrFilePrinter },
        ];
      })
  );

  const redirectToPrinters = () => {
    navigate(`${Routes.EventsV2}/${params.id}/${EventDetailsTab.Printers}`);
  };

  const columns = [
    {
      size: 210,
      accessorKey: 'name',
      header: 'Model type',
      cell: ({ row }) => (
        <Autocomplete
          name={`models.${row.index}.modelId`}
          control={control}
          searchable
          placeholder="Select model type"
          options={modelOptions}
          className="w-[210px] border-0"
          defaultValue={getModelSelectedValue(row.original.modelId)}
        />
      ),
    },
    {
      size: 140,
      accessorKey: 'sizeId',
      header: 'Size',
      cell: ({ row }) => (
        <Autocomplete
          name={`models.${row.index}.sizeId`}
          control={control}
          placeholder="Select size"
          options={optionsData.sizes}
          className="w-[140px] border-0"
        />
      ),
    },
    {
      size: 180,
      accessorKey: 'position',
      header: 'Position',
      cell: ({ row }) => (
        <MultiSelect
          name={`models.${row.index}.position`}
          control={control}
          placeholder="Select position"
          options={positionOptions}
          maxCount={3}
          className="[&_button]:border-0"
        />
      ),
    },
    {
      size: 150,
      accessorKey: 'quantity',
      header: 'Quantity',
      cell: ({ row }) => {
        const fieldName = `models.${row.index}.quantity`;
        return (
          <FormField
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <Input
                  {...field}
                  type="number"
                  autoFocus={fieldName === activeFocusFieldId}
                  onFocus={() => setActiveFocusFieldId(fieldName)}
                  onBlur={() => setActiveFocusFieldId('')}
                  placeholder="Add quantity"
                  className="w-[140px] border-0"
                  step={1}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        );
      },
    },
    {
      accessorKey: 'gcrFile',
      header: 'GCR File',
      cell: ({ row }) => {
        if (!row.original.modelId || !row.original.sizeId) {
          return null;
        }

        if (!filteredPrinters.length) {
          return (
            <Button
              variant="link"
              className="text-destructive"
              startIcon={<WarningIcon />}
              onClick={redirectToPrinters}
            >
              Please add or enable printers for this model
            </Button>
          );
        }

        const { typeId, emptyGcrFilePrinter } =
          gcrModelTypeMapping[
            `${row.original.modelId}_${row.original.sizeId}`
          ] ?? [];

        return emptyGcrFilePrinter ? (
          <Button
            variant="link"
            className="text-destructive"
            startIcon={<WarningIcon />}
            onClick={() =>
              setCreateGcrDetails({
                typeId,
                sizeId: row.original.sizeId,
                printerId: emptyGcrFilePrinter.id,
              })
            }
          >
            Where is no gcr file for {emptyGcrFilePrinter.value} printer
          </Button>
        ) : (
          <span>
            <CircleCheckIcon color={colors.green[400]} size={28} />
          </span>
        );
      },
    },
    {
      size: 40,
      id: 'actions',
      header: 'Action',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          data-testid="remove-model-button"
          size="icon"
          onClick={() => remove(row.index)}
        >
          <TrashIcon size={28} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];

  useEffect(() => {
    const emptyGcrFilePrinter = Object.values(gcrModelTypeMapping).find(
      (item) => item.emptyGcrFilePrinter
    );
    // TODO: add logic
    // onValidate(!!emptyGcrFilePrinter);
  }, [gcrModelTypeMapping]);

  return {
    columns,
    fields,
    isFetching,
    createGcrDetails,
    modelOptions,
    sizeOptions: optionsData.sizes,
    refetchGcrs,
    setCreateGcrDetails,
    append,
    update,
  };
};
